import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, catchError, finalize, map, of, shareReplay, throwError } from 'rxjs';

export interface ApiEnvelope<T> {
  status?: string;
  data?: T;
  error?: unknown;
  [key: string]: unknown;
}

export interface AiHealthData {
  ai_enabled?: boolean;
  chroma_mode?: string;
  chroma_path?: string;
  collection_name?: string;
  collection_count?: number;
  [key: string]: unknown;
}

export interface AiIndexInfoData {
  collection?: string;
  exists?: boolean;
  item_count?: number;
  chroma_path?: string;
  ai_enabled?: boolean;
  chroma_mode?: string;
  message?: string;
  [key: string]: unknown;
}

export interface RagReindexStatusData {
  status?: string;
  started_at?: string;
  finished_at?: string;
  error?: string | null;
  total_docs?: number;
  docs_processed?: number;
  chunks_indexed?: number;
  [key: string]: unknown;
}

export interface ReindexJobResolution {
  jobId: string | null;
  data: AiReindexJobData | null;
  source: 'exact' | 'current' | 'history' | 'none';
  staleRequestedJobId: boolean;
}

export interface AiReindexRequest {
  limit?: number | null;
  reset_collection?: boolean;
}

export interface AiReindexAcceptedData {
  job_id?: string;
  message?: string;
  [key: string]: unknown;
}

export interface AiReindexJobData {
  job_id?: string;
  job_status?: string;
  result?: Record<string, unknown>;
  error?: string | null;
  message?: string;
  total_docs_in_database?: number;
  docs_processed?: number;
  chunks_indexed?: number;
  last_activity_at?: string;
  [key: string]: unknown;
}

export interface AiSearchRequest {
  query_text: string;
  top_k?: number;
  prefer_recent?: boolean;
}

export interface AiSearchResult {
  id?: string;
  note_id?: string;
  title?: string;
  snippet?: string;
  semantic_score?: number;
  status_boost?: number;
  recency_boost?: number;
  final_score?: number;
  [key: string]: unknown;
}

export interface AiSearchData {
  results?: AiSearchResult[];
  [key: string]: unknown;
}

export interface ChromaChunk {
  id: string;
  metadata: Record<string, unknown>;
  document: string;
  chunkIndex?: number;
  heading?: string;
  textLength: number;
}

export interface NoteDebugResult {
  chunks: ChromaChunk[];
  raw: unknown;
}

export interface RetrievalTestRequest {
  question: string;
  k?: number;
}

export interface EndpointAvailability {
  canCreateEmbeddingsForNote: boolean;
  canDeleteEmbeddingsForNote: boolean;
  deleteEmbeddingsEndpoint: string | null;
}

export interface ReindexHistoryEntry {
  job_id?: string;
  job_type: 'bulk' | 'note';
  submitted_at: string;
  started_at?: string;
  finished_at?: string;
  status?: string;
  note_id?: string | null;
  note_title?: string | null;
  total_docs?: number;
  total_docs_in_database?: number;
  processed_count?: number;
  indexed_count?: number;
  skipped_count?: number;
  chunks_indexed?: number;
  error_count?: number;
  error?: string | null;
  [key: string]: unknown;
}

export interface ReindexHistoryData {
  total?: number;
  limit?: number;
  entries?: ReindexHistoryEntry[];
  [key: string]: unknown;
}

@Injectable({
  providedIn: 'root',
})
export class AiDebugService {
  private readonly noteDebugInFlight = new Map<string, Observable<ApiEnvelope<unknown>>>();

  constructor(private readonly http: HttpClient) {}

  getHealth(): Observable<ApiEnvelope<AiHealthData>> {
    return this.http.get<ApiEnvelope<AiHealthData>>('/api/ai/health');
  }

  getIndexInfo(): Observable<ApiEnvelope<AiIndexInfoData>> {
    return this.http.get<ApiEnvelope<AiIndexInfoData>>('/api/ai/index-info');
  }

  getReindexStatus(): Observable<ApiEnvelope<RagReindexStatusData>> {
    return this.getCurrentReindexJob().pipe(
      map((res) => {
        const data = res?.data ?? null;
        const statusData: RagReindexStatusData = {
          status: data?.job_status,
          started_at: typeof data?.['started_at'] === 'string' ? (data['started_at'] as string) : undefined,
          finished_at: typeof data?.['finished_at'] === 'string' ? (data['finished_at'] as string) : undefined,
          error: typeof data?.error === 'string' ? data.error : null,
          total_docs: this.toFiniteNumber((data as any)?.result?.['total_docs']),
          docs_processed:
            this.toFiniteNumber((data as any)?.docs_processed) ??
            this.toFiniteNumber((data as any)?.result?.['processed_count']),
          chunks_indexed:
            this.toFiniteNumber((data as any)?.chunks_indexed) ??
            this.toFiniteNumber((data as any)?.result?.['chunk_reindex']) ??
            this.toFiniteNumber((data as any)?.result?.['chunks_reindexed']),
        };
        return { status: 'success', data: statusData } as ApiEnvelope<RagReindexStatusData>;
      }),
      catchError(() =>
        this.getReindexHistory(1).pipe(
          map((historyRes) => {
            const first = Array.isArray(historyRes?.data?.entries) ? historyRes.data!.entries![0] : undefined;
            const statusData: RagReindexStatusData = {
              status: first?.status,
              started_at: first?.started_at,
              finished_at: first?.finished_at,
              error: first?.error ?? null,
              total_docs: this.toFiniteNumber(first?.total_docs ?? first?.total_docs_in_database),
              docs_processed: this.toFiniteNumber(first?.processed_count),
              chunks_indexed: this.toFiniteNumber(first?.chunks_indexed),
            };
            return { status: 'success', data: statusData } as ApiEnvelope<RagReindexStatusData>;
          }),
          catchError(() => of({ status: 'success', data: {} } as ApiEnvelope<RagReindexStatusData>)),
        ),
      ),
    );
  }

  reindexNotes(payload: AiReindexRequest): Observable<ApiEnvelope<AiReindexAcceptedData>> {
    return this.http.post<ApiEnvelope<AiReindexAcceptedData>>('/api/ai/reindex-notes', payload);
  }

  getReindexJob(jobId: string): Observable<ApiEnvelope<AiReindexJobData>> {
    const normalizedId = String(jobId ?? '').trim();
    if (!normalizedId) {
      return throwError(() => new Error('Missing reindex job id.'));
    }
    const encodedId = encodeURIComponent(normalizedId);
    return this.http.get<ApiEnvelope<AiReindexJobData>>(`/api/ai/reindex-notes/${encodedId}`);
  }

  getCurrentReindexJob(): Observable<ApiEnvelope<AiReindexJobData>> {
    return this.http.get<ApiEnvelope<AiReindexJobData>>('/api/ai/reindex-notes/current');
  }

  resolveReindexJob(jobId: string): Observable<ReindexJobResolution> {
    const requested = String(jobId ?? '').trim();
    return this.getReindexJob(requested).pipe(
      map((res) => ({
        jobId: requested,
        data: res?.data ?? null,
        source: 'exact' as const,
        staleRequestedJobId: false,
      })),
      catchError((err: any) => {
        if (Number(err?.status) !== 404) {
          return throwError(() => err);
        }

        return this.getCurrentReindexJob().pipe(
          map((currentRes) => {
            const data = currentRes?.data ?? null;
            const resolvedId = String(data?.job_id ?? '').trim() || null;
            return {
              jobId: resolvedId,
              data,
              source: 'current' as const,
              staleRequestedJobId: true,
            };
          }),
          catchError((currentErr: any) => {
            if (Number(currentErr?.status) !== 404) {
              return throwError(() => currentErr);
            }

            return this.getReindexHistory(1).pipe(
              map((historyRes) => {
                const first = Array.isArray(historyRes?.data?.entries) ? historyRes.data!.entries![0] : undefined;
                if (!first) {
                  return {
                    jobId: null,
                    data: null,
                    source: 'none' as const,
                    staleRequestedJobId: true,
                  };
                }

                const historyJobId = String(first.job_id ?? '').trim() || null;
                const historyData: AiReindexJobData = {
                  job_id: historyJobId ?? undefined,
                  job_status: first.status,
                  result: {
                    processed_count: first.processed_count,
                    indexed_count: first.indexed_count,
                    skipped_count: first.skipped_count,
                    chunk_reindex: first.chunks_indexed,
                  },
                  error: first.error ?? null,
                  total_docs_in_database: first.total_docs_in_database,
                  docs_processed: first.processed_count,
                  chunks_indexed: first.chunks_indexed,
                  message: first.error ?? undefined,
                };

                return {
                  jobId: historyJobId,
                  data: historyData,
                  source: 'history' as const,
                  staleRequestedJobId: true,
                };
              }),
              catchError(() =>
                of({
                  jobId: null,
                  data: null,
                  source: 'none' as const,
                  staleRequestedJobId: true,
                }),
              ),
            );
          }),
        );
      }),
    );
  }

  clearIndex(): Observable<ApiEnvelope<unknown>> {
    return this.http.post<ApiEnvelope<unknown>>('/api/ai/clear-index', {});
  }

  searchNotes(payload: AiSearchRequest): Observable<ApiEnvelope<AiSearchData>> {
    return this.http.post<ApiEnvelope<AiSearchData>>('/api/ai/search-notes', payload);
  }

  getChromaNoteDebug(noteId: string): Observable<ApiEnvelope<unknown>> {
    const normalizedId = String(noteId ?? '').trim();
    const inFlight = this.noteDebugInFlight.get(normalizedId);
    if (inFlight) {
      return inFlight;
    }

    const encodedId = encodeURIComponent(normalizedId);
    const request$ = this.http
      .get<ApiEnvelope<unknown>>(`/api/ai/index-note/${encodedId}?include_embeddings=true&include_documents=true`)
      .pipe(
        shareReplay(1),
        finalize(() => {
          this.noteDebugInFlight.delete(normalizedId);
        }),
      );

    this.noteDebugInFlight.set(normalizedId, request$);
    return request$;
  }

  runRetrievalTest(payload: RetrievalTestRequest): Observable<ApiEnvelope<unknown>> {
    const topK = payload?.k && Number(payload.k) > 0 ? Math.floor(Number(payload.k)) : 10;
    return this.searchNotes({
      query_text: payload.question,
      top_k: topK,
      prefer_recent: true,
    }).pipe(
      map((searchRes) => {
        const results = Array.isArray(searchRes?.data?.results) ? searchRes.data!.results! : [];
        return {
          status: 'success',
          data: {
            source_endpoint: '/api/ai/search-notes',
            raw_results: results,
            reranked_results: results,
          },
        } as ApiEnvelope<unknown>;
      }),
    );
  }

  reindexSingleNote(noteId: string): Observable<ApiEnvelope<unknown>> {
    const encodedId = encodeURIComponent(String(noteId ?? '').trim());
    return this.http.post<ApiEnvelope<unknown>>(`/api/ai/reindex-note/${encodedId}`, {});
  }

  deleteSingleNoteFromIndex(noteId: string): Observable<ApiEnvelope<unknown>> {
    const encodedId = encodeURIComponent(String(noteId ?? '').trim());
    return this.http.delete<ApiEnvelope<unknown>>(`/api/ai/index-note/${encodedId}`);
  }

  getEndpointAvailability(): EndpointAvailability {
    return {
      canCreateEmbeddingsForNote: true,
      canDeleteEmbeddingsForNote: true,
      deleteEmbeddingsEndpoint: '/api/ai/index-note/{note_id}',
    };
  }

  // Compatibility helper for existing note-detail debug page.
  getNoteDebug(noteId: string): Observable<NoteDebugResult> {
    const trimmed = String(noteId ?? '').trim();
    return this.getChromaNoteDebug(trimmed).pipe(
      map((res) => {
        const payload = (res?.data ?? {}) as Record<string, unknown>;
        return {
          chunks: this.extractChunksFromNoteDebugPayload(payload, trimmed),
          raw: payload,
        };
      }),
    );
  }

  getReindexHistory(limit = 10): Observable<ApiEnvelope<ReindexHistoryData>> {
    return this.http.get<ApiEnvelope<ReindexHistoryData>>(`/api/ai/reindex-history?limit=${limit}`).pipe(
      catchError(() => of({ status: 'success', data: { total: 0, limit, entries: [] } } as ApiEnvelope<ReindexHistoryData>)),
    );
  }

  private extractChunksFromNoteDebugPayload(payload: Record<string, unknown>, noteId: string): ChromaChunk[] {
    const objectArrayCandidates = ['chunks', 'vectors', 'embeddings'];
    for (const key of objectArrayCandidates) {
      const value = payload[key];
      if (Array.isArray(value) && value.some((item) => this.isRecord(item))) {
        return value
          .filter((item) => this.isRecord(item))
          .map((item, index) => this.toChunkFromRecord(item as Record<string, unknown>, noteId, index));
      }
    }

    const ids = this.flattenUnknownArray(payload['ids']);
    const metadatas = this.flattenUnknownArray(payload['metadatas']);
    const documents = this.flattenUnknownArray(payload['documents']);
    const embeddings = this.flattenUnknownArray(payload['embeddings']);
    const itemCount = Math.max(ids.length, metadatas.length, documents.length, embeddings.length);

    if (itemCount <= 0) {
      // Flat single-document response (backend stores one embedding per note)
      if (payload['exists'] !== false && (payload['note_id'] || payload['metadata'])) {
        return [this.chunkFromFlatPayload(payload, noteId)];
      }
      return [];
    }

    const chunks: ChromaChunk[] = [];
    for (let i = 0; i < itemCount; i += 1) {
      const metadata = this.isRecord(metadatas[i]) ? ({ ...(metadatas[i] as Record<string, unknown>) } as Record<string, unknown>) : {};
      const documentText = typeof documents[i] === 'string' ? String(documents[i])
        : this.toOptionalString(
            metadata['text'] ?? metadata['chunk_text'] ?? metadata['content'] ?? metadata['document'],
          ) ?? '';
      const rawEmbedding = embeddings[i];
      if (Array.isArray(rawEmbedding)) {
        metadata['embedding_dimensions'] = rawEmbedding.length;
      }

      const chunkIndex = this.toOptionalNumber(metadata['chunk_index'] ?? metadata['chunkIndex']) ?? i;
      // Use actual document length; fall back to a stored metadata field when text is absent.
      const textLength = documentText.length
        || this.toOptionalNumber(metadata['text_length'] ?? metadata['char_count'] ?? metadata['textLength'])
        || 0;
      chunks.push({
        id: String(ids[i] ?? `${noteId}:${i}`),
        metadata,
        document: documentText,
        chunkIndex,
        heading: this.toOptionalString(metadata['heading']),
        textLength,
      });
    }

    return chunks;
  }

  private chunkFromFlatPayload(payload: Record<string, unknown>, noteId: string): ChromaChunk {
    const meta = this.isRecord(payload['metadata'])
      ? ({ ...(payload['metadata'] as Record<string, unknown>) } as Record<string, unknown>)
      : {};
    const embeddingDim = this.toOptionalNumber(payload['embedding_dimension']);
    if (embeddingDim != null) {
      meta['embedding_dimensions'] = embeddingDim;
    }
    const embeddingPreview = payload['embedding_preview'];
    if (Array.isArray(embeddingPreview)) {
      meta['embedding_preview'] = embeddingPreview;
    }
    const textLength =
      this.toOptionalNumber(payload['document_length']) ??
      this.toOptionalNumber(meta['document_length']) ??
      0;
    const docPreview = this.toOptionalString(payload['document_preview'] as unknown) ?? '';
    return {
      id: String(payload['note_id'] ?? noteId),
      metadata: meta,
      document: docPreview,
      chunkIndex: 0,
      heading: this.toOptionalString(payload['title'] as unknown) ?? this.toOptionalString(meta['title'] as unknown),
      textLength,
    };
  }

  private toChunkFromRecord(item: Record<string, unknown>, noteId: string, index: number): ChromaChunk {
    const metadata = this.isRecord(item['metadata']) ? ({ ...(item['metadata'] as Record<string, unknown>) } as Record<string, unknown>) : {};
    const documentText = String(
      item['document'] ?? item['text'] ?? item['content'] ?? item['snippet']
      ?? item['chunk_text'] ?? item['body']
      ?? metadata['text'] ?? metadata['chunk_text'] ?? metadata['content']
      ?? '',
    );
    const chunkIndex = this.toOptionalNumber(item['chunkIndex'] ?? item['chunk_index'] ?? metadata['chunk_index'] ?? metadata['chunkIndex']) ?? index;
    const textLength = (this.toOptionalNumber(item['textLength'] ?? item['text_length']) ?? documentText.length)
      || (this.toOptionalNumber(metadata['text_length'] ?? metadata['char_count']) ?? 0);
    return {
      id: String(item['id'] ?? item['chunk_id'] ?? `${noteId}:${index}`),
      metadata,
      document: documentText,
      chunkIndex,
      heading: this.toOptionalString(item['heading'] ?? metadata['heading']),
      textLength,
    };
  }

  private flattenUnknownArray(value: unknown): unknown[] {
    if (!Array.isArray(value)) {
      return [];
    }
    if (value.length > 0 && Array.isArray(value[0])) {
      return (value as unknown[]).flat(1);
    }
    return value;
  }

  private toOptionalNumber(value: unknown): number | undefined {
    const num = Number(value);
    return Number.isFinite(num) ? num : undefined;
  }

  private toOptionalString(value: unknown): string | undefined {
    if (typeof value === 'string') {
      const trimmed = value.trim();
      return trimmed.length > 0 ? trimmed : undefined;
    }
    return undefined;
  }

  private isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
  }

  private toFiniteNumber(value: unknown): number | undefined {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? Math.floor(numeric) : undefined;
  }
}
